/**
 * FocusManager Component
 *
 * Comprehensive focus management system for LegalSyncUp
 * Features:
 * - Focus trap for modals and dialogs
 * - Focus restoration when dialogs close
 * - Skip links for main content areas
 * - Focus indicator styling improvements
 * - Focus history tracking
 *
 * @example
 * ```tsx
 * <FocusManager>
 *   <MyApp />
 * </FocusManager>
 * ```
 */

import React, { createContext, useContext, useRef, useCallback, useEffect } from 'react';
import { SkipLink } from '@/components/ui/skip-link';

interface FocusHistoryEntry {
  element: HTMLElement;
  timestamp: number;
}

interface FocusManagerContextValue {
  /** Save the currently focused element */
  saveFocus: () => void;
  /** Restore the previously focused element */
  restoreFocus: () => void;
  /** Move focus to a specific element */
  setFocus: (selector: string | HTMLElement) => void;
  /** Check if an element is focusable */
  isFocusable: (element: HTMLElement) => boolean;
  /** Get all focusable elements within a container */
  getFocusableElements: (container?: HTMLElement) => HTMLElement[];
  /** Move focus to the first focusable element in a container */
  focusFirstElement: (container?: HTMLElement) => void;
  /** Move focus to the last focusable element in a container */
  focusLastElement: (container?: HTMLElement) => void;
}

const FocusManagerContext = createContext<FocusManagerContextValue | null>(null);

export interface FocusManagerProps {
  children: React.ReactNode;
  /** Skip link targets */
  skipLinks?: Array<{
    id: string;
    label: string;
  }>;
  /** Whether to show skip links */
  showSkipLinks?: boolean;
}

/**
 * FocusManager Provider Component
 */
export const FocusManager: React.FC<FocusManagerProps> = ({
  children,
  skipLinks = [
    { id: 'main-content', label: 'Skip to main content' },
    { id: 'main-navigation', label: 'Skip to navigation' },
    { id: 'search', label: 'Skip to search' },
  ],
  showSkipLinks = true,
}) => {
  const focusHistoryRef = useRef<FocusHistoryEntry[]>([]);
  const savedFocusRef = useRef<HTMLElement | null>(null);

  /**
   * Check if an element is focusable
   */
  const isFocusable = useCallback((element: HTMLElement): boolean => {
    if (!element) return false;

    // Check if element is hidden or disabled
    const style = window.getComputedStyle(element);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      element.hasAttribute('disabled') ||
      element.hasAttribute('aria-hidden')
    ) {
      return false;
    }

    // Check if element has a valid tabindex
    const tabindex = element.getAttribute('tabindex');
    if (tabindex !== null && parseInt(tabindex, 10) < 0) {
      return false;
    }

    // Check if element is naturally focusable or has tabindex >= 0
    const focusableSelectors = [
      'a[href]',
      'button',
      'input',
      'select',
      'textarea',
      '[tabindex]',
      '[contenteditable="true"]',
    ];

    return focusableSelectors.some((selector) => element.matches(selector));
  }, []);

  /**
   * Get all focusable elements within a container
   */
  const getFocusableElements = useCallback(
    (container: HTMLElement = document.body): HTMLElement[] => {
      const focusableSelectors = [
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'a[href]',
        '[tabindex]:not([tabindex="-1"])',
        '[contenteditable="true"]',
      ].join(', ');

      const elements = Array.from(
        container.querySelectorAll<HTMLElement>(focusableSelectors)
      );

      return elements.filter((element) => isFocusable(element));
    },
    [isFocusable]
  );

  /**
   * Save the currently focused element
   */
  const saveFocus = useCallback(() => {
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && activeElement !== document.body) {
      savedFocusRef.current = activeElement;
      focusHistoryRef.current.push({
        element: activeElement,
        timestamp: Date.now(),
      });

      // Limit history to last 10 items
      if (focusHistoryRef.current.length > 10) {
        focusHistoryRef.current.shift();
      }
    }
  }, []);

  /**
   * Restore the previously focused element
   */
  const restoreFocus = useCallback(() => {
    if (savedFocusRef.current && document.body.contains(savedFocusRef.current)) {
      savedFocusRef.current.focus();
      savedFocusRef.current = null;
    } else {
      // Try to restore from history
      while (focusHistoryRef.current.length > 0) {
        const entry = focusHistoryRef.current.pop();
        if (entry && document.body.contains(entry.element)) {
          entry.element.focus();
          break;
        }
      }
    }
  }, []);

  /**
   * Move focus to a specific element
   */
  const setFocus = useCallback((selector: string | HTMLElement) => {
    const element =
      typeof selector === 'string'
        ? document.querySelector<HTMLElement>(selector)
        : selector;

    if (element && isFocusable(element)) {
      element.focus();
    }
  }, [isFocusable]);

  /**
   * Move focus to the first focusable element in a container
   */
  const focusFirstElement = useCallback(
    (container: HTMLElement = document.body) => {
      const elements = getFocusableElements(container);
      if (elements.length > 0) {
        elements[0].focus();
      }
    },
    [getFocusableElements]
  );

  /**
   * Move focus to the last focusable element in a container
   */
  const focusLastElement = useCallback(
    (container: HTMLElement = document.body) => {
      const elements = getFocusableElements(container);
      if (elements.length > 0) {
        elements[elements.length - 1].focus();
      }
    },
    [getFocusableElements]
  );

  const contextValue: FocusManagerContextValue = {
    saveFocus,
    restoreFocus,
    setFocus,
    isFocusable,
    getFocusableElements,
    focusFirstElement,
    focusLastElement,
  };

  return (
    <FocusManagerContext.Provider value={contextValue}>
      {/* Skip Links */}
      {showSkipLinks && (
        <div className="skip-links">
          {skipLinks.map((link) => (
            <SkipLink key={link.id} href={`#${link.id}`}>
              {link.label}
            </SkipLink>
          ))}
        </div>
      )}

      {/* Global Focus Styles */}
      <style>{`
        /* Enhanced focus indicators for better visibility */
        *:focus-visible {
          outline: 2px solid hsl(var(--ring));
          outline-offset: 2px;
          border-radius: 2px;
        }

        /* Focus within interactive elements */
        button:focus-visible,
        a:focus-visible,
        input:focus-visible,
        select:focus-visible,
        textarea:focus-visible,
        [role="button"]:focus-visible,
        [role="link"]:focus-visible,
        [tabindex]:focus-visible {
          outline: 2px solid hsl(var(--ring));
          outline-offset: 2px;
        }

        /* Card and container focus */
        [role="article"]:focus-visible,
        [role="region"]:focus-visible,
        .card:focus-visible {
          outline: 2px solid hsl(var(--ring));
          outline-offset: 4px;
        }

        /* Table row focus */
        tr:focus-visible {
          outline: 2px solid hsl(var(--ring));
          outline-offset: -2px;
        }

        /* Remove outline for mouse users (but keep for keyboard) */
        *:focus:not(:focus-visible) {
          outline: none;
        }

        /* Skip links visibility */
        .skip-links {
          position: relative;
          z-index: 10000;
        }
      `}</style>

      {children}
    </FocusManagerContext.Provider>
  );
};

/**
 * Hook to access focus manager functionality
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFocusManager(): FocusManagerContextValue {
  const context = useContext(FocusManagerContext);

  if (!context) {
    throw new Error('useFocusManager must be used within a FocusManager');
  }

  return context;
}

/**
 * Hook for focus trap functionality (alternative to FocusTrap component)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement>,
  enabled: boolean = true
) {
  const { saveFocus, restoreFocus, getFocusableElements } = useFocusManager();

  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    // Save focus when trap is enabled
    saveFocus();

    const container = containerRef.current;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements(container);
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    // Focus first element
    const focusableElements = getFocusableElements(container);
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      // Restore focus when trap is disabled
      restoreFocus();
    };
  }, [enabled, containerRef, saveFocus, restoreFocus, getFocusableElements]);
}

/**
 * Component for creating focus traps using the hook
 */
export interface FocusTrapRegionProps {
  children: React.ReactNode;
  enabled?: boolean;
  className?: string;
}

export const FocusTrapRegion: React.FC<FocusTrapRegionProps> = ({
  children,
  enabled = true,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(containerRef as React.RefObject<HTMLElement>, enabled);

  return (
    <div ref={containerRef} className={className} data-focus-trap={enabled}>
      {children}
    </div>
  );
};

export default FocusManager;
